import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingContextType {
  showTour: boolean;
  startTour: () => void;
  closeTour: () => void;
  currentStep: number;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  totalSteps: number;
  showWelcomePrompt: boolean;
  dismissWelcomePrompt: () => void;
  acceptWelcomePrompt: () => void;
  highlightRoute: string | null;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const TOUR_COMPLETED_KEY = 'focustrack_tour_completed';

export interface TourStep {
  title: string;
  description: string;
  icon: string;
  route?: string;
}

export const tourSteps: TourStep[] = [
  {
    title: 'Dashboard',
    description: 'Sua central de controle! Veja o resumo do dia, estatísticas, streak de estudos e recomendações personalizadas.',
    icon: '📊',
    route: '/',
  },
  {
    title: 'Disciplinas',
    description: 'Cadastre suas matérias, organize conteúdos e defina pesos para o cronograma inteligente.',
    icon: '📚',
    route: '/disciplinas',
  },
  {
    title: 'Registrar Sessão',
    description: 'Registre cada sessão de estudo com tempo, questões resolvidas e páginas lidas. Tudo é contabilizado automaticamente.',
    icon: '✍️',
  },
  {
    title: 'Timer Pomodoro',
    description: 'Use o timer integrado com técnica Pomodoro para manter o foco. Ao finalizar, a sessão é registrada automaticamente.',
    icon: '⏱️',
  },
  {
    title: 'Revisões Espaçadas',
    description: 'O sistema agenda revisões automáticas dos conteúdos estudados para reforçar a memorização de longo prazo.',
    icon: '🔄',
    route: '/revisoes',
  },
  {
    title: 'Plano de Estudos com IA',
    description: 'Gere um cronograma semanal personalizado com inteligência artificial, baseado nos seus pesos e disponibilidade.',
    icon: '🧠',
    route: '/plano',
  },
  {
    title: 'Estatísticas e Progresso',
    description: 'Acompanhe gráficos detalhados de evolução, taxa de acerto, tempo investido e muito mais.',
    icon: '📈',
    route: '/estatisticas',
  },
  {
    title: 'Simulados',
    description: 'Registre seus simulados e acompanhe a evolução da nota ao longo do tempo.',
    icon: '📝',
    route: '/simulados',
  },
  {
    title: 'Conquistas',
    description: 'Desbloqueie badges conforme avança nos estudos. Gamificação para manter a motivação!',
    icon: '🏆',
    route: '/conquistas',
  },
];

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(false);

  useEffect(() => {
    if (user) {
      const completed = localStorage.getItem(`${TOUR_COMPLETED_KEY}_${user.id}`);
      if (!completed) {
        // Small delay so the dashboard renders first
        const timer = setTimeout(() => setShowWelcomePrompt(true), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const navigateToStep = useCallback((stepIndex: number) => {
    const route = tourSteps[stepIndex]?.route;
    if (route) navigate(route);
  }, [navigate]);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setShowTour(true);
    setShowWelcomePrompt(false);
    navigateToStep(0);
  }, [navigateToStep]);

  const closeTour = useCallback(() => {
    setShowTour(false);
    setCurrentStep(0);
    if (user) {
      localStorage.setItem(`${TOUR_COMPLETED_KEY}_${user.id}`, 'true');
    }
  }, [user]);

  const nextStep = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      navigateToStep(next);
    } else {
      closeTour();
    }
  }, [currentStep, closeTour, navigateToStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      navigateToStep(prev);
    }
  }, [currentStep, navigateToStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < tourSteps.length) {
      setCurrentStep(step);
      navigateToStep(step);
    }
  }, [navigateToStep]);

  const dismissWelcomePrompt = useCallback(() => {
    setShowWelcomePrompt(false);
    if (user) {
      localStorage.setItem(`${TOUR_COMPLETED_KEY}_${user.id}`, 'true');
    }
  }, [user]);

  const acceptWelcomePrompt = useCallback(() => {
    startTour();
  }, [startTour]);

  return (
    <OnboardingContext.Provider
      value={{
        showTour,
        startTour,
        closeTour,
        currentStep,
        nextStep,
        prevStep,
        goToStep,
        totalSteps: tourSteps.length,
        showWelcomePrompt,
        dismissWelcomePrompt,
        acceptWelcomePrompt,
        highlightRoute: showTour ? (tourSteps[currentStep]?.route ?? null) : null,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
